import { FileArrowUp } from "@ignite-analytics/icons";
import { Alert, Button, Stack, Typography } from "@mui/material";
import React, { useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { DocumentCard } from "./DocumentCard";
import { showFile } from "./fileActions";
import {
    type AnswerSuggestion,
    PREFILL_EXTENSIONS,
    PREFILL_MAX_BYTES,
    PREFILL_MAX_FILES,
    type Prefill,
    prefillAccepts,
} from "./prefill";

type Props = {
    prefill: Prefill;
    onSuggestions: (suggestions: AnswerSuggestion[]) => number;
};

type Status =
    | { kind: "idle" }
    | { kind: "scanning"; files: File[] }
    | { kind: "done"; applied: number }
    | { kind: "invalid" }
    | { kind: "failed" };

export const PrefillPanel: React.FC<Props> = ({ prefill, onSuggestions }) => {
    const { formatMessage } = useIntl();
    const input = useRef<HTMLInputElement>(null);
    const [status, setStatus] = useState<Status>({ kind: "idle" });
    const scanning = status.kind === "scanning";

    const scan = async (files: File[]) => {
        if (files.length === 0) return;
        const total = files.reduce((sum, f) => sum + f.size, 0);
        if (
            files.length > PREFILL_MAX_FILES ||
            total > PREFILL_MAX_BYTES ||
            !files.every((f) => prefillAccepts(f.name))
        ) {
            setStatus({ kind: "invalid" });
            return;
        }
        setStatus({ kind: "scanning", files });
        try {
            const applied = onSuggestions(await prefill.add(files));
            setStatus({ kind: "done", applied });
        } catch {
            setStatus({ kind: "failed" });
        }
    };

    const pending = status.kind === "scanning" ? status.files : [];

    return (
        <Stack spacing={2} sx={{ px: { xs: 2, sm: 3 }, py: 3, alignItems: "flex-start" }}>
            <Stack spacing={0.5}>
                <Typography variant="textMd" fontWeight={600}>
                    <FormattedMessage defaultMessage="Supporting documents" />
                </Typography>
                <Typography variant="textSm" color="textTextSecondary">
                    <FormattedMessage defaultMessage="Add documents such as annual reports, policies or certificates, and we use them to help you fill in the questionnaire." />
                </Typography>
            </Stack>
            {prefill.documents.length + pending.length > 0 && (
                <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1.5 }}>
                    {prefill.documents.map((doc) => (
                        <DocumentCard
                            key={doc.key}
                            name={doc.name}
                            sizeBytes={doc.size}
                            onOpen={doc.open}
                            onRemove={() => void doc.remove().catch(() => undefined)}
                            removing={scanning}
                        />
                    ))}
                    {pending.map((file, i) => (
                        <DocumentCard
                            key={`pending-${i}`}
                            name={file.name}
                            sizeBytes={file.size}
                            onOpen={() => showFile(file, file.name)}
                            analyzing
                        />
                    ))}
                </Stack>
            )}
            <Button
                size="small"
                color="secondary"
                variant="outlined"
                startIcon={<FileArrowUp />}
                disabled={scanning}
                onClick={() => input.current?.click()}
            >
                <FormattedMessage defaultMessage="Upload files" />
            </Button>
            <input
                ref={input}
                type="file"
                hidden
                multiple
                accept={PREFILL_EXTENSIONS.join(",")}
                aria-label={formatMessage({ defaultMessage: "Upload files" })}
                onChange={(event) => {
                    const files = Array.from(event.target.files ?? []);
                    event.target.value = "";
                    void scan(files);
                }}
            />
            {status.kind === "invalid" && (
                <Alert severity="warning" sx={{ alignSelf: "stretch" }}>
                    <FormattedMessage
                        defaultMessage="Upload up to {max} PDF, image or text files, 20 MB in total."
                        values={{ max: PREFILL_MAX_FILES }}
                    />
                </Alert>
            )}
            {status.kind === "failed" && (
                <Alert severity="error" sx={{ alignSelf: "stretch" }}>
                    <FormattedMessage defaultMessage="We could not read the documents. Please try again." />
                </Alert>
            )}
            {status.kind === "done" && status.applied === 0 && (
                <Typography variant="textSm" color="textTextSecondary">
                    <FormattedMessage defaultMessage="No answers found for the unanswered questions." />
                </Typography>
            )}
        </Stack>
    );
};
